import React, { useEffect, useMemo, useState, useContext } from "react";
import { Clock } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import SectionCard from "./ui/SectionCard";
import OrdersFilters from "./ui/OrdersFilters";
import OrdersTable from "./ui/OrdersTable";
import OrderDetailModal from "./ui/OrderDetailModal";
import Pagination from "./ui/Pagination";
import DeliveryServiceType from "./ui/DeliveryServiceType";
import OrderPaidDetail from "./ui/OrderPaidDetail";
import OrderEventsModal from "./ui/OrderEventsModal";

import { toInputDate } from "./utils/date";
import { useOrdersData } from "./hooks/useOrdersData";
import useOrderActions from "./hooks/useOrderActions";
import { GlobalContext } from "../../Context/GlobalContext";
import useOrders from "../Services/OrderServices/useOrders";

const PAGE_SIZE = 20;

export default function Orders() {
  const {
    AdminPk,
    clientOptions,
    newOrders,
    historyOrders,
    loadingNew,
    loadingHis,
    getNewOrder,
    getHistory,

    getOrderEvents,
  } = useOrdersData();
  const { getOrderDetails, checkEasebuzzTransaction } = useOrders();

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const focus = searchParams.get("focus");

    if (
      [
        "current-orders",
      ,
      ].includes(focus)
    ) {
      setActiveTab("current");
      return;
    }

    if (
      [
        "order-history",
     
      ].includes(focus)
    ) {
      setActiveTab("history");
    }
  }, [searchParams]);

  const { ordersUpdateTick, wsConnected } = useContext(GlobalContext);

  const [activeTab, setActiveTab] = useState("current");
  const [client, setClient] = useState("");
  const [search, setSearch] = useState("");
  const [easebuzzOpen, setEasebuzzOpen] = useState(false);
  const [easebuzzLoading, setEasebuzzLoading] = useState(false);
  const [easebuzzData, setEasebuzzData] = useState(null);
  const [easebuzzOrder, setEasebuzzOrder] = useState(null);
  const today = useMemo(() => new Date(), []);
  const [currentFrom, setCurrentFrom] = useState(toInputDate(today));
  const [currentTo, setCurrentTo] = useState(toInputDate(today));

  const dEnd = new Date();
  const dStart = new Date();
  dStart.setDate(dEnd.getDate() - 7);

  const [historyFrom, setHistoryFrom] = useState(toInputDate(dStart));
  const [historyTo, setHistoryTo] = useState(toInputDate(dEnd));

  const [pageCurrent, setPageCurrent] = useState(1);
  const [pageHistory, setPageHistory] = useState(1);

  // Order detail modal
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const [paymentOrder, setPaymentOrder] = useState(null);
  const [paidOrder, setPaidOrder] = useState(null);
  const [eventsOpen, setEventsOpen] = useState(false);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsOrderId, setEventsOrderId] = useState(null);
  const [orderEvents, setOrderEvents] = useState([]);
useEffect(() => {
  if (!client && clientOptions?.length > 0) {
    const defaultClient =
      clientOptions.find(
        (c) =>
          c?.value?.toLowerCase() === "chaafo" ||
          c?.us_code?.toLowerCase() === "chaafo" ||
          c?.label?.toLowerCase().includes("chaafo")
      ) || clientOptions[0];

    setClient(
      defaultClient?.value ||
      defaultClient?.us_code ||
      ""
    );
  }
}, [clientOptions, client]);

  const { accept, prepared, dispatched, delivered, reject, mark_paid } =
    useOrderActions({
      refresh: async () => {
        if (activeTab === "current") {
          await getNewOrder({ client, from: currentFrom, to: currentTo });
        } else {
          await getHistory({ client, from: historyFrom, to: historyTo });
        }
      },
    });

  // Client-side search
  const applySearch = (rows) => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      return (
        String(r.orderid || "")
          .toLowerCase()
          .includes(q) ||
        String(r.customer_name || "")
          .toLowerCase()
          .includes(q) ||
        String(r.customer_mobile || "")
          .toLowerCase()
          .includes(q) ||
        String(r.organization_name || "")
          .toLowerCase()
          .includes(q)
      );
    });
  };

  const rowsCurrent = applySearch(newOrders || []);
  const rowsHistory = applySearch(historyOrders || []);

  const totalCurrent = rowsCurrent.length;
  const totalHistory = rowsHistory.length;

  // Initial + when admin / tab / client changes
  useEffect(() => {
    if (!AdminPk) return;
    if (activeTab === "current") {
      getNewOrder({ client, from: currentFrom, to: currentTo });
    } else {
      getHistory({ client, from: historyFrom, to: historyTo });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [AdminPk, activeTab, client]);

  // Re-fetch on date change – current tab
  useEffect(() => {
    if (!AdminPk || activeTab !== "current") return;
    setPageCurrent(1);
    getNewOrder({ client, from: currentFrom, to: currentTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFrom, currentTo]);

  // Re-fetch on date change – history tab
  useEffect(() => {
    if (!AdminPk || activeTab !== "history") return;
    setPageHistory(1);
    getHistory({ client, from: historyFrom, to: historyTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyFrom, historyTo]);

  // WebSocket pe active tab refresh
  useEffect(() => {
    if (!AdminPk) return;
    if (!ordersUpdateTick) return;
    if (activeTab === "current") {
      getNewOrder({ client, from: currentFrom, to: currentTo });
    } else {
      getHistory({ client, from: historyFrom, to: historyTo });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersUpdateTick]);

  const refreshActive = () => {
    if (activeTab === "current") {
      getNewOrder({ client, from: currentFrom, to: currentTo });
    } else {
      getHistory({ client, from: historyFrom, to: historyTo });
    }
  };

  const onCloseDetails = () => {
    setOpen(false);
    setSelected(null);
    setDetail(null);
  };

  const onAction = async (key, ord) => {
    const map = { accept, prepared, dispatched, delivered, reject, mark_paid };
    if (map[key]) await map[key](ord);
  };

  const onView = async (row) => {
    setSelected(row);
    setOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const d = await getOrderDetails(row?.orderid);
      setDetail(d);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleClosePaymentModal = () => {
    setPaymentOrder(null);
  };

  const handlePaymentSuccess = async () => {
    setPaymentOrder(null);
    refreshActive();
  };

  const handleStatusAction = (statusKey, ord) => {
    if (activeTab === "history") {
      return;
    }

    const orderType = (ord?.orderType || "").toLowerCase();

    if (orderType === "home_delivery") {
      if (statusKey === "paid") {
        setPaidOrder(ord);
        setOpen(false);
      }
      return;
    }

    if (statusKey === "unpaid") {
      setPaymentOrder(ord);
    } else if (statusKey === "paid") {
      setPaidOrder(ord);
      setOpen(false);
    }
  };
  const onDelayClick = async (row) => {
    const oid = row?.orderid;
    if (!oid) return;

    setEventsOpen(true);
    setEventsOrderId(oid);
    setOrderEvents([]);
    setEventsLoading(true);

    try {
      const ev = await getOrderEvents(oid);
      setOrderEvents(ev || []);
    } finally {
      setEventsLoading(false);
    }
  };
  const onEasebuzzStatusClick = async (row) => {
    const txnID = row?.txnId || row?.txnid || row?.transactionId;

    if (!txnID) {
      return;
    }

    setEasebuzzOrder(row);
    setEasebuzzOpen(true);
    setEasebuzzLoading(true);
    setEasebuzzData(null);

    try {
      const res = await checkEasebuzzTransaction(txnID);

      if (res?.success) {
        setEasebuzzData(res?.data || null);
      } else {
        setEasebuzzData(res?.data || null);
      }
    } finally {
      setEasebuzzLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-emerald-50 text-emerald-600">
            <Clock size={18} />
          </div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold">All Orders</h1>

            <span
              className={`inline-flex items-center gap-1 rounded-full px-2 py-1 mt-1 ${wsConnected
                ? "bg-emerald-50 text-emerald-700"
                : "bg-red-50 text-red-600"
                }`}
            >
              <span
                className={`h-2 w-2 rounded-full ${wsConnected ? "bg-emerald-500" : "bg-red-500"
                  }`}
              ></span>
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center rounded-xl bg-gray-100 p-1">
          <button
            onClick={() => setActiveTab("current")}
            className={`h-8 rounded-lg px-3 text-xs font-semibold transition ${activeTab === "current"
              ? "bg-white shadow-sm"
              : "text-gray-600 hover:text-gray-800"
              }`}
          >
            Current Orders
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`h-8 rounded-lg px-3 text-xs font-semibold transition ${activeTab === "history"
              ? "bg-white shadow-sm"
              : "text-gray-600 hover:text-gray-800"
              }`}
          >
            Order History
          </button>
        </div>
      </div>

      <OrdersFilters
        activeTab={activeTab}
        client={client}
        setClient={(v) => {
          setClient(v);
          setPageCurrent(1);
          setPageHistory(1);
        }}
        clientOptions={clientOptions}
        fromDate={activeTab === "current" ? currentFrom : historyFrom}
        setFromDate={activeTab === "current" ? setCurrentFrom : setHistoryFrom}
        toDate={activeTab === "current" ? currentTo : historyTo}
        setToDate={activeTab === "current" ? setCurrentTo : setHistoryTo}
        search={search}
        setSearch={(v) => {
          setSearch(v);
          setPageCurrent(1);
          setPageHistory(1);
        }}
        onRefresh={refreshActive}
      />

      {/* Table + Pagination */}
      <SectionCard
        title={activeTab === "current" ? "Current Orders" : "Order History"}
        icon={<Clock size={16} />}
      >
        {activeTab === "current" ? (
          <>
            <OrdersTable
              rows={rowsCurrent}
              loading={loadingNew}
              onView={onView}
              onDelay={onDelayClick}
              onEasebuzzStatusClick={onEasebuzzStatusClick}
              page={pageCurrent}
              pageSize={PAGE_SIZE}
            />

            <Pagination
              page={pageCurrent}
              total={totalCurrent}
              pageSize={PAGE_SIZE}
              onChange={setPageCurrent}
            />
          </>
        ) : (
          <>
            <OrdersTable
              rows={rowsHistory}
              loading={loadingHis}
              onView={onView}
              onDelay={onDelayClick}
              onEasebuzzStatusClick={onEasebuzzStatusClick}
              page={pageHistory}
              pageSize={PAGE_SIZE}
            />
            <Pagination
              page={pageHistory}
              total={totalHistory}
              pageSize={PAGE_SIZE}
              onChange={setPageHistory}
            />
          </>
        )}
      </SectionCard>

      {/* Details Modal */}
      <OrderDetailModal
        open={open}
        onClose={onCloseDetails}
        loading={detailLoading}
        order={selected}
        detail={detail}
        onAction={onAction}
        onStatusAction={handleStatusAction}
        isHistoryTab={activeTab === "history"}
      />

      {/* Cash Payment Modal (UNPAID) */}
      <DeliveryServiceType
        show={!!paymentOrder}
        onClose={handleClosePaymentModal}
        order={paymentOrder}
        amount={
          paymentOrder?.order_amount ||
          paymentOrder?.total_amount ||
          paymentOrder?.net_amount
        }
        onSuccess={handlePaymentSuccess}
      />

      {/* OrderPaidDetail Modal (PAID) */}
      <OrderPaidDetail
        show={!!paidOrder}
        onClose={() => setPaidOrder(null)}
        order={paidOrder}
        amount={
          paidOrder?.totalOrderPrice ||
          paidOrder?.pay_amount ||
          paidOrder?.order_amount ||
          paidOrder?.total_amount ||
          paidOrder?.net_amount
        }
        onSuccess={async () => {
          setPaidOrder(null);
          refreshActive();
        }}
      />
      <OrderEventsModal
        open={eventsOpen}
        onClose={() => {
          setEventsOpen(false);
          setEventsOrderId(null);
          setOrderEvents([]);
        }}
        loading={eventsLoading}
        orderId={eventsOrderId}
        events={orderEvents}
      />
      {easebuzzOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold">Easebuzz Transaction Status</h2>
                <p className="text-xs text-gray-500">
                  Order ID: {easebuzzOrder?.orderid || "-"} | Txn ID:{" "}
                  {easebuzzOrder?.txnId || easebuzzOrder?.txnid || "-"}
                </p>
              </div>
              <button
                onClick={() => {
                  setEasebuzzOpen(false);
                  setEasebuzzData(null);
                  setEasebuzzOrder(null);
                }}
                className="rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="max-h-[70vh] overflow-auto p-5">
              {easebuzzLoading ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  Loading...
                </div>
              ) : !easebuzzData ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  No transaction data found.
                </div>
              ) : (
                <>
                  <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="rounded-xl bg-gray-50 p-3">
                      <div className="text-xs text-gray-500">Response Code</div>
                      <div className="font-semibold">
                        {easebuzzData?.responseCode || "-"}
                      </div>
                    </div>
                    <div className="rounded-xl bg-gray-50 p-3">
                      <div className="text-xs text-gray-500">Message</div>
                      <div className="font-semibold">
                        {easebuzzData?.responseMessage || "-"}
                      </div>
                    </div>
                  </div>

                  {Array.isArray(easebuzzData?.response?.msg) &&
                    easebuzzData.response.msg.length > 0 ? (
                    <div className="space-y-3">
                      {easebuzzData.response.msg.map((item, idx) => (
                        <div key={idx} className="rounded-2xl border p-4">
                          <div className="mb-3 text-sm font-semibold text-gray-800">
                            Transaction Detail
                          </div>

                          <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                            <div><span className="font-medium">Txn ID:</span> {item?.txnid || "-"}</div>
                            <div><span className="font-medium">Status:</span> {item?.status || "-"}</div>
                            <div><span className="font-medium">Amount:</span> {item?.amount || "-"}</div>
                            <div><span className="font-medium">Net Amount:</span> {item?.net_amount_debit || "-"}</div>
                            <div><span className="font-medium">Payment Source:</span> {item?.payment_source || "-"}</div>
                            <div><span className="font-medium">Mode:</span> {item?.mode || "-"}</div>
                            <div><span className="font-medium">Easepay ID:</span> {item?.easepayid || "-"}</div>
                            <div><span className="font-medium">Bank Ref No:</span> {item?.bank_ref_num || "-"}</div>
                            <div><span className="font-medium">UPI VA:</span> {item?.upi_va || "-"}</div>
                            <div><span className="font-medium">Added On:</span> {item?.addedon || "-"}</div>
                            <div><span className="font-medium">Firstname:</span> {item?.firstname || "-"}</div>
                            <div><span className="font-medium">Phone:</span> {item?.phone || "-"}</div>
                            <div className="md:col-span-2">
                              <span className="font-medium">Error:</span> {item?.error || "-"}
                            </div>
                            <div className="md:col-span-2">
                              <span className="font-medium">Error Message:</span> {item?.error_Message || "-"}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <pre className="overflow-auto rounded-xl bg-gray-50 p-4 text-xs">
                      {JSON.stringify(easebuzzData, null, 2)}
                    </pre>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
